<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/services/api.js';

const route = useRoute();
const router = useRouter();
const API_URL = "https://sigaf.tamep.bo/api"; // Asegúrate de incluir /api

const inicializando = ref(true);
const activo = ref(null);

const opciones = reactive({ 
  regionales: [],
  usuarios: [] 
});

const listaUsuariosFiltrados = ref([]);

const modalBaja = reactive({
  visible: false,
  form: {
    regional_id: '',
    usuario_id: '',
    motivo: '',
    archivo: null
  }
});

onMounted(async () => {
  try {
    // 1. Cargamos datos del activo y los datos necesarios para el formulario de baja
    const [resActivo, resForm] = await Promise.all([
      api.get(`${API_URL}/activos/${route.params.id}/`),
      api.get(`${API_URL}/operacion-baja/datos_formulario/`)
    ]);
    
    activo.value = resActivo.data;
    opciones.regionales = resForm.data.regionales;
    opciones.usuarios = resForm.data.usuarios;

    // Si el activo ya está asignado, intentamos pre-cargar su regional actual
    if (activo.value.ubicacion_actual_detalle) {
        modalBaja.form.regional_id = activo.value.ubicacion_actual_detalle.regional;
        filtrarUsuarios();
    }
  } catch (error) {
    console.error("Error al cargar datos:", error);
  } finally {
    inicializando.value = false;
  }
});

const filtrarUsuarios = () => {
  // Filtramos los usuarios por la regional seleccionada
  // Nota: Asegúrate que tu UserSerializer devuelva el ID de regional
  listaUsuariosFiltrados.value = opciones.usuarios.filter(u => u.regional_id == modalBaja.form.regional_id);
};

const handleFile = (e) => {
  modalBaja.form.archivo = e.target.files[0];
};

const confirmarBaja = async () => {
  if (!modalBaja.form.motivo || !modalBaja.form.usuario_id || !modalBaja.form.regional_id) {
    return alert("Todos los campos marcados son obligatorios para el Acta de Baja.");
  }

  const fd = new FormData();
  
  // Sincronizado con BajaActivoViewSet.procesar_baja
  fd.append('regional_id', modalBaja.form.regional_id);
  fd.append('usuario_id', modalBaja.form.usuario_id);
  fd.append('motivo', modalBaja.form.motivo);
  
  if (modalBaja.form.archivo) {
    fd.append('documento', modalBaja.form.archivo);
  }

  try {
    // Endpoint: /api/operacion-baja/{id}/procesar_baja/
    await api.post(`${API_URL}/operacion-baja/${activo.value.id}/procesar_baja/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    alert("Activo dado de baja y desvinculado correctamente.");
    router.push('/activos');
  } catch (error) {
    console.error(error);
  }
};
</script>
<template>
  <div class="container-fluid py-3 py-md-5 app-bg-tamep app-transition min-vh-100">
    <div v-if="inicializando" class="text-center py-5">
      <div class="spinner-border text-title-custom" role="status"></div>
      <p class="mt-3 text-custom-muted fw-bold">Verificando Estado del Activo Fijo...</p>
    </div>

    <div v-else-if="activo" class="row justify-content-center">
      <div class="col-12 col-md-8 col-lg-6">
        <div class="card card-custom border-0 shadow-soft mb-4">
          <div class="card-body p-4 p-md-5 card-body-custom text-center">
            
            <div class="icon-wrap mx-auto mb-4 text-danger">
              <i class="fas fa-file-invoice fa-4x"></i>
            </div>
            <h4 class="fw-bolder text-title-custom mb-2">Baja Definitiva</h4>
            <p class="text-custom-muted text-sm">Este proceso es irreversible y desvinculará el activo del inventario operativo.</p>
            
            <div class="bg-section-custom p-4 border-radius-modern border border-custom-divider shadow-inner text-start my-4 my-md-5">
              <div class="row g-3">
                <div class="col-6">
                  <label class="label-custom">Código TAMEP</label>
                  <p class="fw-bold mb-0 text-navy-slate fs-5">{{ activo.codigo_tamep }}</p>
                </div>
                <div class="col-6 text-end">
                  <label class="label-custom">Situación Actual</label>
                  <p :class="['mb-0 fw-bold fs-5', activo.situacion_activo === 'Baja' ? 'text-danger' : 'text-success']">
                    {{ activo.situacion_activo }}
                  </p>
                </div>
              </div>
            </div>

            <button 
              @click="modalBaja.visible = true" 
              class="btn-baja-danger w-100 shadow-modern py-3 text-wrap"
              :disabled="activo.situacion_activo === 'Baja'"
            >
              <i class="fas fa-gavel me-2"></i>
              {{ activo.situacion_activo === 'Baja' ? 'ACTIVO YA DADO DE BAJA' : 'INICIAR PROCESO DE BAJA' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="modalBaja.visible" class="tamep-modal-overlay">
      <div class="tamep-modal-content card-custom p-3 p-md-4 animate__animated animate__fadeInDown border border-custom-divider">
        <div class="modal-header-tamep mb-4 text-white text-center p-3 border-radius-modern shadow-soft" style="background-color: #DC2626;">
          <h6 class="mb-0 text-white font-weight-bold text-sm"><i class="fas fa-gavel me-2"></i>ACTA DE BAJA TÉCNICA</h6>
        </div>

        <div class="row g-3 text-start">
          <div class="col-12">
            <label class="label-custom">Regional Administrativa</label>
            <select v-model="modalBaja.form.regional_id" @change="filtrarUsuarios" class="form-select-modern input-custom">
              <option value="">Seleccione Regional...</option>
              <option v-for="r in opciones.regionales" :key="r.id" :value="r.id">{{ r.nombre }}</option>
            </select>
          </div>

          <div class="col-12">
            <label class="label-custom text-navy-slate fw-800">Responsable de Activos / Autorizador</label>
            <select v-model="modalBaja.form.usuario_id" :disabled="!modalBaja.form.regional_id" class="form-select-modern border-primary-light input-custom text-navy-slate">
              <option value="">Seleccione responsable...</option>
              <option v-for="u in listaUsuariosFiltrados" :key="u.id" :value="u.id">{{ u.nombre_completo }}</option>
            </select>
          </div>

          <div class="col-12">
            <label class="label-custom">Justificación Técnica del Retiro</label>
            <textarea v-model="modalBaja.form.motivo" class="form-control modern-input input-custom" rows="3" placeholder="Describa el estado físico y motivo de la baja..."></textarea>
          </div>

          <div class="col-12 mb-2">
            <label class="label-custom text-danger fw-800 text-wrap">Documento de Respaldo (PDF Firmado)</label>
            <input type="file" @change="handleFile" class="form-control modern-input-file input-custom" accept=".pdf">
          </div>
        </div>

        <div class="row g-2 mt-4">
          <div class="col-12 col-sm-6 order-2 order-sm-1">
            <button @click="modalBaja.visible = false" class="btn btn-outline-secondary btn-custom-outline w-100 py-3 border-radius-modern">VOLVER</button>
          </div>
          <div class="col-12 col-sm-6 order-1 order-sm-2">
            <button @click="confirmarBaja" class="btn-baja-danger w-100 py-3 mb-0">
              CONFIRMAR
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* =========================================
   ESTILO TAMEP MODERN & MINIMALIST COMPARTIDO
   ========================================= */

/* Transiciones Generales */
.app-transition { transition: all 0.3s ease; }

/* Variables de Modo Claro */
.app-bg-tamep { background-color: #f8fafc; }
.card-custom { background-color: #ffffff; border-radius: 16px; transition: all 0.3s ease, box-shadow 0.3s ease; }
.card-body-custom { background-color: #ffffff; transition: background-color 0.3s ease; border-radius: 16px;}
.text-custom { color: #1e293b; transition: color 0.3s ease; }
.text-title-custom { color: #003366; transition: color 0.3s ease; }
.text-custom-muted { color: #64748b; transition: color 0.3s ease; }
.text-navy-slate { color: #1e293b; }
.bg-navy-slate { background-color: #1e293b; }
.border-custom-divider { border-color: #f1f5f9 !important; transition: border-color 0.3s ease; }
.shadow-soft { box-shadow: 0 10px 25px rgba(29, 52, 82, 0.03); }
.shadow-modern { box-shadow: 0 7px 14px rgba(21, 31, 48, 0.08); }
.shadow-inner { box-shadow: inset 0 2px 4px rgba(0,0,0,0.03); }

/* Controles de Formulario MODERNOS */
.input-custom { background-color: #ffffff; color: #1e293b; border-color: #e2e8f0; transition: all 0.3s ease; }
.form-control-modern, .form-select-modern, .modern-input { width: 100%; padding: 0.8rem; border-radius: 8px; border: 1px solid #e2e8f0; font-size: 0.875rem; background-color: #ffffff; color: #1e293b; transition: all 0.2s ease;}
.form-control-modern:focus, .form-select-modern:focus, .modern-input:focus { border-color: #003366; outline: none; box-shadow: 0 0 0 3px rgba(0, 51, 102, 0.08); }
.form-control-modern::placeholder { color: #cbd5e1; }
select:disabled, input:disabled { background-color: #f1f5f9 !important; opacity: 1; cursor: not-allowed; border-color: #e2e8f0 !important;}

/* File Input Modernizado */
.modern-input-file { border: 1px solid #e2e8f0; padding: 0.7rem; border-radius: 8px; font-size: 0.8rem; cursor: pointer; color: #64748b;}
.modern-input-file::-webkit-file-upload-button { background: #f1f5f9; border: none; padding: 6px 12px; border-radius: 5px; color: #1e293b; font-weight: 600; margin-right: 10px;}
.modern-input-file:hover { border-color: #003366; }

/* Elementos Especiales */
.border-radius-modern { border-radius: 8px; }
.label-custom { font-size: 0.72rem; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 7px; display: block; transition: color 0.3s ease;}
.fw-800 { font-weight: 800 !important; }
.border-primary-light { border-color: #00336680 !important; }

/* Contenedores secundarios */
.bg-section-custom { background-color: #f8fafc; transition: background-color 0.3s ease; }
.icon-wrap { text-align: center; }

/* Botones MODERNOS */
.btn-baja-danger { background: #DC2626; color: white; border: none; padding: 15px; border-radius: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; transition: all 0.3s ease; font-size: 0.85rem;}
.btn-baja-danger:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(220, 38, 38, 0.15) !important; background-color: #B91C1C;}
.btn-baja-danger:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-custom-outline { color: #1e293b; border-color: #1e293b; font-weight: 700; transition: all 0.3s ease;}
.btn-custom-outline:hover { background-color: #f1f5f9; color: #0F172A; border-color: #0F172A; }

/* MODAL OVERLAY */
.tamep-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(15, 23, 42, 0.85); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(4px); }
.tamep-modal-content { width: 90%; max-width: 500px; }


/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
:global(.dark-mode) .app-bg-tamep { background-color: #121212; }
:global(.dark-mode) .card-custom, 
:global(.dark-mode) .card-body-custom { background-color: #1e1e1e !important; box-shadow: 0 10px 25px rgba(0,0,0,0.5) !important; border: 1px solid #2d2d2d !important; }
:global(.dark-mode) .border-custom-divider { border-color: #333333 !important; }

:global(.dark-mode) .text-custom,
:global(.dark-mode) .text-navy-slate { color: #e0e0e0 !important; }
:global(.dark-mode) .text-title-custom { color: #ffffff !important; }
:global(.dark-mode) .text-custom-muted { color: #aaaaaa !important; }

/* Controles de Formulario Oscuros */
:global(.dark-mode) .input-custom,
:global(.dark-mode) .form-control-modern, 
:global(.dark-mode) .form-select-modern, 
:global(.dark-mode) .modern-input { 
  background-color: #2a2a2a !important; 
  color: #e0e0e0 !important; 
  border-color: #444444 !important; 
}
:global(.dark-mode) .input-custom:focus { border-color: #4dabf7 !important; box-shadow: 0 0 0 3px rgba(77, 171, 247, 0.15) !important; }
:global(.dark-mode) select:disabled, 
:global(.dark-mode) input:disabled { background-color: #1a1a1a !important; color: #666 !important; border-color: #333 !important; opacity: 1; }
:global(.dark-mode) .modern-input-file { border-color: #444; color: #aaa;}
:global(.dark-mode) .modern-input-file::-webkit-file-upload-button { background: #333; color: #fff;}

/* Elementos Especiales en Oscuro */
:global(.dark-mode) .bg-section-custom { background-color: #121212 !important; border-color: #333 !important; }
:global(.dark-mode) .btn-custom-outline { color: #e0e0e0 !important; border-color: #555 !important; }
:global(.dark-mode) .btn-custom-outline:hover { background-color: #333 !important; color: #fff !important; border-color: #777 !important; }
</style>